import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { median } from "./valuation-calibration";
import { parseListing } from "./valuation-parse";
import { SEGMENTS } from "./valuation-core";

export interface MarketRow {
  key: string;
  label: string;
  n: number;
  medianPrice: number;
  lowPrice: number;
  highPrice: number;
  medianYear: number;
}

export interface MarketStats {
  updatedAt: string;
  total: number;
  categories: { category: "husbil" | "husvagn"; n: number; medianPrice: number }[];
  segments: MarketRow[];
  brands: MarketRow[];
  years: MarketRow[];
}

function summarise(key: string, label: string, rows: { price: number; year: number }[]): MarketRow {
  const prices = rows.map((r) => r.price).sort((a, b) => a - b);
  return {
    key,
    label,
    n: rows.length,
    medianPrice: Math.round(median(prices)),
    lowPrice: prices[Math.floor(prices.length * 0.15)] ?? prices[0] ?? 0,
    highPrice: prices[Math.floor(prices.length * 0.85)] ?? prices[prices.length - 1] ?? 0,
    medianYear: Math.round(median(rows.map((r) => r.year))),
  };
}

/** Publik marknadsstatistik ur vårt eget annonsmaterial. Inga persondata. */
export const getMarketStats = createServerFn({ method: "GET" }).handler(
  async (): Promise<MarketStats> => {
    const empty: MarketStats = {
      updatedAt: new Date().toISOString(),
      total: 0,
      categories: [],
      segments: [],
      brands: [],
      years: [],
    };

    const supabase = createClient(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );

    const { data, error } = await supabase
      .from("vehicle_snapshots")
      .select("title, price, category, last_seen_at")
      .in("category", ["husbil", "husvagn"])
      .not("price", "is", null)
      .limit(2000);

    if (error || !data?.length) return empty;

    type Row = { price: number; year: number; segment: string; brand: string; category: string };
    const rows: Row[] = [];

    for (const r of data as { title: string | null; price: number | null; category: string | null }[]) {
      const parsed = parseListing(r.title, r.category);
      if (!parsed || !r.price || r.price < 30_000 || parsed.quality < 0.6) continue;
      rows.push({
        price: r.price,
        year: parsed.year,
        segment: parsed.segment,
        brand: parsed.brand.name,
        category: SEGMENTS[parsed.segment].category,
      });
    }

    if (!rows.length) return empty;

    const group = <T,>(items: Row[], by: (r: Row) => T) => {
      const map = new Map<T, Row[]>();
      for (const item of items) {
        const k = by(item);
        map.set(k, [...(map.get(k) ?? []), item]);
      }
      return map;
    };

    const segments = [...group(rows, (r) => r.segment)]
      .filter(([, v]) => v.length >= 3)
      .map(([k, v]) => summarise(String(k), SEGMENTS[k as keyof typeof SEGMENTS].label, v))
      .sort((a, b) => b.n - a.n);

    const brands = [...group(rows, (r) => r.brand)]
      .filter(([, v]) => v.length >= 3)
      .map(([k, v]) => summarise(String(k), String(k), v))
      .sort((a, b) => b.n - a.n)
      .slice(0, 12);

    const years = [...group(rows, (r) => r.year)]
      .filter(([, v]) => v.length >= 3)
      .map(([k, v]) => summarise(String(k), String(k), v))
      .sort((a, b) => Number(b.key) - Number(a.key))
      .slice(0, 12);

    const categories = (["husbil", "husvagn"] as const)
      .map((category) => {
        const subset = rows.filter((r) => r.category === category);
        return {
          category,
          n: subset.length,
          medianPrice: Math.round(median(subset.map((r) => r.price))),
        };
      })
      .filter((c) => c.n > 0);

    return {
      updatedAt: new Date().toISOString(),
      total: rows.length,
      categories,
      segments,
      brands,
      years,
    };
  },
);
